import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import { Notification } from '../../services/notificationService';
import {
  Typography,
  Box,
  CircularProgress,
  Divider,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Link as RouterLink } from 'react-router-dom';
import PageShell, { SurfaceCard } from '../../components/common/PageShell';
import { notificationLink, notificationTarget } from '../../utils/notificationLinks';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const formatDate = (dateString) => {
    try {
      const locales = { en: enUS, tr: tr };
      const locale = locales[i18n.language] || enUS;
      return format(new Date(dateString), 'PPP', { locale });
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      try {
        const response = await Notification();
        if (response.statusCode === 200) {
          setNotifications(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  return (
    <PageShell
      maxWidth="md"
      title={t('notification.title')}
      subtitle={!isLoading && notifications.length === 0 ? t('notification.empty') : undefined}
      icon={<NotificationsNoneIcon />}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      ) : notifications.length > 0 ? (
        <Stack spacing={1.5}>
          {notifications.map((notification) => {
            // Mülk Takibi (type 20) bildirimleri data.pid/tab ile ilgili mülk sekmesine; kiracı portalı (21/22/23) ev sahibinde
            // mülk ödemeler/mesajlar bölümüne, kiracıda /kiraci/{tid} sayfasına bağlanır; kayıtlı arama özeti (24) /kayitli-aramalar'a; diğerleri tıklanmaz
            const link = notificationLink(notification);
            const target = link ? notificationTarget(notification) : null;
            return (
              <SurfaceCard
                key={notification.id}
                {...(link ? { component: RouterLink, to: link } : {})}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  display: 'block',
                  color: 'inherit',
                  textDecoration: 'none',
                  transition: 'border-color 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    transform: 'translateY(-1px)',
                  },
                  ...(link && {
                    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                  }),
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {notification.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                  {notification.text}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(notification.createdDate)}
                  </Typography>
                  {link && (
                    <Typography variant="caption" color="primary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
                      {t(
                        target === 'savedSearch'
                          ? 'savedSearch.notificationOpen'
                          : target === 'tenant'
                            ? 'tenantPortal.notificationOpen'
                            : 'propertyTracking.notifications.open'
                      )}
                      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} aria-hidden />
                    </Typography>
                  )}
                </Box>
              </SurfaceCard>
            );
          })}
        </Stack>
      ) : (
        <SurfaceCard sx={{ p: 5, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('notification.empty')}</Typography>
        </SurfaceCard>
      )}
    </PageShell>
  );
};

export default NotificationsPage;
